<template>
  <div class="page-container dash">
    <!-- Welcome banner with the headline number -->
    <section class="dash-hero dash-rise">
      <span class="dash-blob dash-blob--one" aria-hidden="true"></span>
      <span class="dash-blob dash-blob--two" aria-hidden="true"></span>

      <div class="dash-hero-main">
        <div class="dash-hero-kicker">
          <v-icon size="16">mdi-calendar-blank-outline</v-icon>
          <span>{{ today }}</span>
          <span class="dash-role-chip">{{ panelLabel }}</span>
        </div>
        <h1 class="dash-hero-title">{{ greeting }}, {{ userName }}</h1>
        <p class="dash-hero-sub">{{ config.subtitle }}</p>
        <div class="dash-hero-actions">
          <router-link v-if="deskRoute" :to="deskRoute" class="dash-hero-btn dash-hero-btn--solid">
            <v-icon size="18">mdi-briefcase-check-outline</v-icon>
            Open my desk
            <span v-if="deskTotal" class="dash-hero-btn-badge">{{ deskTotal }}</span>
          </router-link>
          <router-link v-if="config.listRoute" :to="config.listRoute" class="dash-hero-btn" :class="{ 'dash-hero-btn--solid': !deskRoute }">
            <v-icon size="18">mdi-filmstrip</v-icon>
            View applications
          </router-link>
          <router-link v-if="superAdmin" :to="usersRoute" class="dash-hero-btn">
            <v-icon size="18">mdi-account-group-outline</v-icon>
            Manage users
          </router-link>
        </div>
      </div>

      <div class="dash-hero-figure">
        <div class="dash-hero-figure-text">
          <span class="dash-hero-figure-label">
            <span class="dash-reel" aria-hidden="true"><v-icon size="14">mdi-movie-roll</v-icon></span>
            Total applications
          </span>
          <div class="dash-hero-figure-value">
            <count-up v-if="loaded" :value="counts.total" />
            <span v-else>–</span>
          </div>
          <div class="dash-hero-delta" :class="'dash-hero-delta--' + yearDelta.tone">
            <template v-if="yearDelta.pct !== null">
              <v-icon size="16">{{ yearDelta.pct >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}</v-icon>
              {{ yearDelta.pct >= 0 ? '+' : '' }}{{ yearDelta.pct }}%
              <span class="dash-hero-delta-note">{{ yearDelta.note }}</span>
            </template>
            <span v-else class="dash-hero-delta-note">{{ formatNumber(baseYearTotal) }} received in {{ year }}</span>
          </div>
        </div>
        <div class="dash-hero-spark">
          <apexchart v-if="loaded" type="area" height="62" :options="sparkOptions" :series="sparkSeries"></apexchart>
          <div class="dash-hero-figure-foot">Per month, {{ year }}</div>
        </div>
      </div>
    </section>

    <!-- Status tiles -->
    <div class="dash-tiles">
      <component
        :is="tile.to ? 'router-link' : 'div'"
        v-for="(tile, i) in statusTiles"
        :key="tile.key"
        :to="tile.to"
        class="dash-tile dash-rise"
        :style="{ '--i': i + 1, '--tone': tile.color, '--tone-soft': tile.soft }"
      >
        <span class="dash-tile-top">
          <span class="dash-tile-icon"><v-icon size="22">{{ tile.icon }}</v-icon></span>
          <span class="dash-tile-share">{{ share(counts[tile.key]) }}%</span>
        </span>
        <span class="dash-tile-value">
          <count-up v-if="loaded" :value="counts[tile.key]" />
          <span v-else>–</span>
        </span>
        <span class="dash-tile-label">{{ tile.label }}</span>
        <span class="dash-meter" :aria-label="share(counts[tile.key]) + '% of all applications'">
          <span class="dash-meter-fill" :style="{ width: (loaded ? share(counts[tile.key]) : 0) + '%' }"></span>
        </span>
        <v-icon v-if="tile.to" size="18" class="dash-tile-go">mdi-arrow-right</v-icon>
      </component>
    </div>

    <!-- Filters for the charts below -->
    <section class="dash-filters dash-rise" style="--i: 5">
      <div class="dash-filters-main">
        <div class="dash-filters-label">
          <v-icon size="18">mdi-filter-variant</v-icon>
          Status
        </div>
        <div v-if="extras" class="dash-chips" role="group" aria-label="Application status">
          <button
            v-for="f in statusFilters"
            :key="f.key"
            type="button"
            class="dash-chip"
            :class="{ 'dash-chip--on': statusFilter === f.key }"
            :aria-pressed="statusFilter === f.key ? 'true' : 'false'"
            @click="setStatusFilter(f.key)"
          >
            <v-icon size="16">{{ f.icon }}</v-icon>
            {{ f.label }}
            <span class="dash-chip-count">{{ loaded ? formatNumber(counts[f.count]) : '–' }}</span>
          </button>
        </div>
        <div v-else class="dash-filters-note">All applications</div>
      </div>
      <div class="dash-filters-side">
        <span class="dash-filters-hint">{{ extras ? 'Status applies to the monthly chart, insights and latest applications' : '' }}</span>
        <v-select
          v-model="year"
          :items="years"
          dense
          outlined
          hide-details
          class="dash-year"
          aria-label="Year"
          prepend-inner-icon="mdi-calendar-month-outline"
          :menu-props="{ offsetY: true }"
        ></v-select>
      </div>
    </section>

    <div class="dash-grid">
      <!-- Applications per month: this year against last year -->
      <section class="surface dash-card dash-span-2 dash-rise" style="--i: 6">
        <header class="dash-card-head">
          <div>
            <div class="dash-card-title">
              Applications per month
              <span v-if="statusFilter !== 'all'" class="dash-filter-tag">{{ statusFilterLabel }}</span>
            </div>
            <div class="dash-card-sub">{{ year }} compared with {{ year - 1 }}</div>
          </div>
          <div class="dash-card-tools">
            <div class="dash-switch" role="group" aria-label="Show as">
              <button
                type="button"
                class="dash-switch-btn"
                :class="{ 'dash-switch-btn--on': trendView === 'chart' }"
                :aria-pressed="trendView === 'chart' ? 'true' : 'false'"
                title="Chart"
                @click="trendView = 'chart'"
              >
                <v-icon size="18">mdi-chart-areaspline</v-icon>
              </button>
              <button
                type="button"
                class="dash-switch-btn"
                :class="{ 'dash-switch-btn--on': trendView === 'table' }"
                :aria-pressed="trendView === 'table' ? 'true' : 'false'"
                title="Table"
                @click="trendView = 'table'"
              >
                <v-icon size="18">mdi-table</v-icon>
              </button>
            </div>
          </div>
        </header>
        <div class="dash-card-body" :class="{ 'dash-refreshing': refreshing }">
          <v-skeleton-loader v-if="!loaded" type="image" height="300"></v-skeleton-loader>
          <apexchart v-else-if="trendView === 'chart'" type="line" height="310" :options="trendOptions" :series="trendSeries"></apexchart>
          <div v-else class="dash-table-wrap">
            <table class="dash-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th class="num">{{ year }}</th>
                  <th class="num">{{ year - 1 }}</th>
                  <th class="num">Change</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(m, i) in months" :key="m">
                  <td>{{ m }}</td>
                  <td class="num">{{ monthly[i] }}</td>
                  <td class="num">{{ prevMonthly[i] }}</td>
                  <td class="num" :class="changeClass(monthly[i] - prevMonthly[i])">{{ signed(monthly[i] - prevMonthly[i]) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td class="num">{{ yearTotal }}</td>
                  <td class="num">{{ prevYearTotal }}</td>
                  <td class="num" :class="changeClass(yearTotal - prevYearTotal)">{{ signed(yearTotal - prevYearTotal) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </section>

      <!-- Status split -->
      <section class="surface dash-card dash-rise" style="--i: 6">
        <header class="dash-card-head">
          <div>
            <div class="dash-card-title">Status split</div>
            <div class="dash-card-sub">Where all {{ formatNumber(counts.total) }} applications stand</div>
          </div>
        </header>
        <div class="dash-card-body">
          <v-skeleton-loader v-if="!loaded" type="image" height="220"></v-skeleton-loader>
          <div v-else-if="!statusSum" class="empty-state">
            <v-icon size="44">mdi-chart-donut</v-icon>
            No applications yet.
          </div>
          <template v-else>
            <apexchart type="donut" height="230" :options="donutOptions" :series="donutSeries"></apexchart>
            <ul class="dash-legend">
              <li v-for="tile in statusTiles" :key="tile.key">
                <span class="dash-legend-dot" :style="{ background: tile.color }"></span>
                <span class="dash-legend-name">{{ tile.label }}</span>
                <span class="dash-legend-value">{{ formatNumber(counts[tile.key]) }}</span>
                <span class="dash-legend-pct">{{ share(counts[tile.key]) }}%</span>
              </li>
            </ul>
          </template>
        </div>
      </section>

      <!-- Fees received and refunds -->
      <section v-if="money" class="surface dash-card dash-span-2 dash-rise" style="--i: 8">
        <header class="dash-card-head">
          <div>
            <div class="dash-card-title">Fees and refunds</div>
            <div class="dash-card-sub">Fees received and refunds paid per month, {{ year }}</div>
          </div>
          <div class="dash-switch" role="group" aria-label="Show as">
            <button
              type="button"
              class="dash-switch-btn"
              :class="{ 'dash-switch-btn--on': moneyView === 'chart' }"
              :aria-pressed="moneyView === 'chart' ? 'true' : 'false'"
              title="Chart"
              @click="moneyView = 'chart'"
            >
              <v-icon size="18">mdi-chart-bar</v-icon>
            </button>
            <button
              type="button"
              class="dash-switch-btn"
              :class="{ 'dash-switch-btn--on': moneyView === 'table' }"
              :aria-pressed="moneyView === 'table' ? 'true' : 'false'"
              title="Table"
              @click="moneyView = 'table'"
            >
              <v-icon size="18">mdi-table</v-icon>
            </button>
          </div>
        </header>

        <div class="money-tiles">
          <div v-for="t in moneyTiles" :key="t.key" class="money-tile" :style="{ '--tone': t.color, '--tone-soft': t.soft }">
            <span class="money-tile-icon"><v-icon size="20">{{ t.icon }}</v-icon></span>
            <span class="money-tile-text">
              <span class="money-tile-label">{{ t.label }}</span>
              <span class="money-tile-value">
                <count-up :value="t.value" :prefix="t.money ? '₹' : ''" />
              </span>
              <span class="money-tile-note">{{ t.note }}</span>
            </span>
          </div>
        </div>

        <div class="dash-card-body" :class="{ 'dash-refreshing': refreshing }">
          <apexchart v-if="moneyView === 'chart'" type="bar" height="280" :options="moneyOptions" :series="moneySeries"></apexchart>
          <div v-else class="dash-table-wrap">
            <table class="dash-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th class="num">Fees received</th>
                  <th class="num">Refunds paid</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(m, i) in months" :key="m">
                  <td>{{ m }}</td>
                  <td class="num">{{ rupees(money.payments.monthly[i]) }}</td>
                  <td class="num">{{ rupees(money.refunds.monthly[i]) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>Total {{ year }}</td>
                  <td class="num">{{ rupees(feesThisYear) }}</td>
                  <td class="num">{{ rupees(refundsThisYear) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </section>

      <!-- Fees kept after refunds, and refund status -->
      <section v-if="money" class="surface dash-card dash-rise" style="--i: 9">
        <header class="dash-card-head">
          <div>
            <div class="dash-card-title">Fees kept</div>
            <div class="dash-card-sub">Fees received less refunds paid, all time</div>
          </div>
        </header>
        <div class="dash-card-body">
          <div class="net-value"><count-up :value="netKept" prefix="₹" /></div>
          <div class="net-bar" role="img" :aria-label="(100 - refundShare) + '% kept, ' + refundShare + '% refunded'">
            <span class="net-bar-part net-bar-part--kept" :style="{ flexGrow: Math.max(100 - refundShare, 0) }"></span>
            <span v-if="refundShare > 0" class="net-bar-part net-bar-part--refunded" :style="{ flexGrow: refundShare }"></span>
          </div>
          <ul class="net-legend">
            <li>
              <span class="dash-legend-dot" style="background: #15803d"></span>
              <span class="net-legend-name">Kept</span>
              <b>{{ rupees(netKept) }}</b>
              <span class="net-legend-pct">{{ 100 - refundShare }}%</span>
            </li>
            <li>
              <span class="dash-legend-dot" style="background: #2563eb"></span>
              <span class="net-legend-name">Refunded</span>
              <b>{{ rupees(money.refunds.completed.amount) }}</b>
              <span class="net-legend-pct">{{ refundShare }}%</span>
            </li>
          </ul>

          <div class="net-subtitle">Refund status</div>
          <ul class="refund-list">
            <li v-for="r in refundRows" :key="r.key" :style="{ '--tone': r.color, '--tone-soft': r.soft }">
              <span class="refund-icon"><v-icon size="18">{{ r.icon }}</v-icon></span>
              <span class="refund-name">{{ r.label }}</span>
              <span class="refund-count">{{ formatNumber(r.count) }}</span>
              <span class="refund-amount">{{ rupees(r.amount) }}</span>
            </li>
          </ul>
        </div>
      </section>

      <!-- Waiting at my desk (office roles) -->
      <section v-if="deskStages.length" class="surface dash-card dash-span-2 dash-rise" style="--i: 7">
        <header class="dash-card-head">
          <div>
            <div class="dash-card-title">
              Waiting at your desk
              <span class="dash-badge">{{ loaded ? deskTotal : '–' }}</span>
            </div>
            <div class="dash-card-sub">Applications you can act on now, by stage</div>
          </div>
          <v-btn depressed class="btn-act btn-act--sm btn-act--primary" :to="deskRoute">
            Open desk
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
        </header>
        <div class="desk-grid" :class="'desk-grid--' + deskStages.length">
          <router-link
            v-for="(s, i) in deskStages"
            :key="s.task"
            :to="{ path: deskRoute, query: { stage: String(s.stage) } }"
            class="desk-card"
            :style="{ '--i': i }"
          >
            <span class="desk-card-slate">
              <span class="desk-card-stripes"></span>
              <span class="desk-card-scene">{{ s.tag.toUpperCase() }}</span>
            </span>
            <span class="desk-card-main">
              <span class="desk-card-icon"><v-icon size="22">{{ s.icon }}</v-icon></span>
              <span class="desk-card-count">
                <count-up v-if="loaded" :value="s.count" />
                <span v-else>–</span>
              </span>
            </span>
            <span class="desk-card-name">{{ s.name }}</span>
            <span class="desk-card-hint">{{ s.hint }}</span>
            <span class="desk-card-foot">
              <span class="dash-meter">
                <span class="dash-meter-fill" :style="{ width: (loaded && deskTotal ? Math.round((s.count / deskTotal) * 100) : 0) + '%' }"></span>
              </span>
              <span class="desk-card-share">{{ deskTotal ? Math.round((s.count / deskTotal) * 100) : 0 }}% of desk</span>
            </span>
          </router-link>
        </div>
      </section>

      <!-- Latest applications (no desk: beside the insights) -->
      <section v-if="!deskStages.length" class="surface dash-card dash-span-2 dash-rise" style="--i: 7">
        <header class="dash-card-head">
          <div>
            <div class="dash-card-title">Latest applications</div>
            <div class="dash-card-sub">Most recent in your {{ config.scopeLabel }}</div>
          </div>
          <div class="recent-head-actions">
            <v-btn
              v-if="canDeleteApps && recent.length"
              depressed
              class="btn-act btn-act--sm"
              :class="selectMode ? 'btn-act--primary' : 'btn-act--neutral'"
              @click="toggleSelectMode"
            >
              <v-icon left small>{{ selectMode ? "mdi-check" : "mdi-checkbox-multiple-outline" }}</v-icon>
              {{ selectMode ? "Done" : "Select" }}
            </v-btn>
            <v-btn v-if="config.listRoute" depressed class="btn-act btn-act--sm btn-act--primary" :to="config.listRoute">
              View all
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </header>
        <div v-if="selectMode" class="bulk-bar" :class="{ 'bulk-bar--active': selectedCount > 0 }">
          <div class="bulk-bar-info">
            <v-icon small :color="selectedCount ? '#b91c1c' : '#64748b'">{{ selectedCount ? "mdi-checkbox-marked-circle-outline" : "mdi-gesture-tap" }}</v-icon>
            <span v-if="selectedCount" class="bulk-bar-count">{{ selectedCount }} application{{ selectedCount === 1 ? "" : "s" }} selected</span>
            <span v-else class="bulk-bar-hint">Tick the applications you want to delete.</span>
          </div>
          <div class="bulk-bar-actions">
            <v-btn text small class="text-none font-weight-bold" color="#15803d" @click="toggleAll(!allSelected)">{{ allSelected ? "Unselect all" : "Select all" }}</v-btn>
            <v-btn v-if="selectedCount" text small class="text-none font-weight-bold" color="#64748b" @click="clearSelection">Clear</v-btn>
            <v-btn depressed class="btn-act btn-act--sm btn-act--danger" :disabled="!selectedCount" @click="confirmDialog = true">
              <v-icon small left>mdi-delete-outline</v-icon>
              Delete{{ selectedCount ? ` (${selectedCount})` : "" }}
            </v-btn>
          </div>
        </div>
        <div v-if="loaded && !recent.length" class="empty-state">
          <v-icon size="44">mdi-filmstrip-off</v-icon>
          No applications yet.
        </div>
        <ul v-else class="recent-list">
          <li v-for="(app, i) in recentItems" :key="app._id || i" class="dash-rise" :style="{ '--i': i + 8 }">
            <component
              :is="app.to && !selectMode ? 'router-link' : 'div'"
              :to="app.to"
              class="recent-item"
              :class="{ 'recent-item--select': selectMode, 'recent-item--selected': selectMode && isSelected(app) }"
              @click="selectMode && toggleItem(app)"
            >
              <v-simple-checkbox
                v-if="selectMode"
                :value="isSelected(app)"
                color="#b91c1c"
                :ripple="false"
                class="recent-check"
                @click.native.stop
                @input="toggleItem(app)"
              ></v-simple-checkbox>
              <span class="recent-icon"><v-icon size="18">mdi-movie-open-outline</v-icon></span>
              <span class="recent-body">
                <span class="recent-title">#{{ app.applicationNo }} · {{ app.applicantName || 'Applicant' }}</span>
                <span class="recent-sub">{{ app.natureOfApplication || 'Film shooting' }} · {{ taskLabel(app) }}</span>
              </span>
              <span class="recent-side">
                <span class="status-pill" :class="'status-pill--' + statusOf(app).tone">{{ statusOf(app).label }}</span>
                <span class="recent-when">{{ ago(app.created_at) }}</span>
              </span>
            </component>
          </li>
        </ul>
      </section>

      <!-- Insights -->
      <section class="surface dash-card dash-rise" style="--i: 8">
        <header class="dash-card-head">
          <div>
            <div class="dash-card-title">Insights</div>
            <div class="dash-card-sub">How {{ year }} is going</div>
          </div>
        </header>
        <div class="dash-card-body">
          <v-skeleton-loader v-if="!loaded" type="image" height="180"></v-skeleton-loader>
          <apexchart v-else type="radialBar" height="200" :options="gaugeOptions" :series="[approvalRate]"></apexchart>
          <ul class="dash-insights">
            <li v-for="row in insights" :key="row.label">
              <span class="dash-insight-icon"><v-icon size="18">{{ row.icon }}</v-icon></span>
              <span class="dash-insight-label">{{ row.label }}</span>
              <span class="dash-insight-value">{{ loaded ? row.value : '–' }}</span>
            </li>
          </ul>
        </div>
      </section>

      <!-- Latest applications (desk roles: full width under the desk) -->
      <section v-if="deskStages.length" class="surface dash-card dash-span-3 dash-rise" style="--i: 9">
        <header class="dash-card-head">
          <div>
            <div class="dash-card-title">Latest applications</div>
            <div class="dash-card-sub">Most recent in your {{ config.scopeLabel }}</div>
          </div>
          <div v-if="canDeleteApps && recent.length" class="recent-head-actions">
            <v-btn depressed class="btn-act btn-act--sm" :class="selectMode ? 'btn-act--primary' : 'btn-act--neutral'" @click="toggleSelectMode">
              <v-icon left small>{{ selectMode ? "mdi-check" : "mdi-checkbox-multiple-outline" }}</v-icon>
              {{ selectMode ? "Done" : "Select" }}
            </v-btn>
          </div>
        </header>
        <div v-if="selectMode" class="bulk-bar" :class="{ 'bulk-bar--active': selectedCount > 0 }">
          <div class="bulk-bar-info">
            <v-icon small :color="selectedCount ? '#b91c1c' : '#64748b'">{{ selectedCount ? "mdi-checkbox-marked-circle-outline" : "mdi-gesture-tap" }}</v-icon>
            <span v-if="selectedCount" class="bulk-bar-count">{{ selectedCount }} application{{ selectedCount === 1 ? "" : "s" }} selected</span>
            <span v-else class="bulk-bar-hint">Tick the applications you want to delete.</span>
          </div>
          <div class="bulk-bar-actions">
            <v-btn text small class="text-none font-weight-bold" color="#15803d" @click="toggleAll(!allSelected)">{{ allSelected ? "Unselect all" : "Select all" }}</v-btn>
            <v-btn v-if="selectedCount" text small class="text-none font-weight-bold" color="#64748b" @click="clearSelection">Clear</v-btn>
            <v-btn depressed class="btn-act btn-act--sm btn-act--danger" :disabled="!selectedCount" @click="confirmDialog = true">
              <v-icon small left>mdi-delete-outline</v-icon>
              Delete{{ selectedCount ? ` (${selectedCount})` : "" }}
            </v-btn>
          </div>
        </div>
        <div v-if="loaded && !recent.length" class="empty-state">
          <v-icon size="44">mdi-filmstrip-off</v-icon>
          No applications yet.
        </div>
        <ul v-else class="recent-list recent-list--grid">
          <li v-for="(app, i) in recentItems" :key="app._id || i" class="dash-rise" :style="{ '--i': i + 10 }">
            <component
              :is="app.to && !selectMode ? 'router-link' : 'div'"
              :to="app.to"
              class="recent-item"
              :class="{ 'recent-item--select': selectMode, 'recent-item--selected': selectMode && isSelected(app) }"
              @click="selectMode && toggleItem(app)"
            >
              <v-simple-checkbox
                v-if="selectMode"
                :value="isSelected(app)"
                color="#b91c1c"
                :ripple="false"
                class="recent-check"
                @click.native.stop
                @input="toggleItem(app)"
              ></v-simple-checkbox>
              <span class="recent-icon"><v-icon size="18">mdi-movie-open-outline</v-icon></span>
              <span class="recent-body">
                <span class="recent-title">#{{ app.applicationNo }} · {{ app.applicantName || 'Applicant' }}</span>
                <span class="recent-sub">{{ app.natureOfApplication || 'Film shooting' }} · {{ taskLabel(app) }}</span>
              </span>
              <span class="recent-side">
                <span class="status-pill" :class="'status-pill--' + statusOf(app).tone">{{ statusOf(app).label }}</span>
                <span class="recent-when">{{ ago(app.created_at) }}</span>
              </span>
            </component>
          </li>
        </ul>
      </section>

      <!-- User accounts by role (super admin) -->
      <section v-if="superAdmin" class="surface dash-card dash-span-3 dash-rise" style="--i: 10">
        <header class="dash-card-head">
          <div>
            <div class="dash-card-title">User accounts</div>
            <div class="dash-card-sub">Accounts by role · each role opens its own users page</div>
          </div>
          <v-btn depressed class="btn-act btn-act--sm btn-act--primary" :to="usersRoute">
            Manage users
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
        </header>
        <div class="acc-body">
          <div class="acc-summary">
            <div class="acc-total-label">Total accounts</div>
            <div class="acc-total">
              <count-up v-if="accounts" :value="accounts.total || 0" />
              <span v-else>–</span>
            </div>
            <div class="acc-split" aria-hidden="true">
              <span class="acc-split-active" :style="{ width: activeShare + '%' }"></span>
            </div>
            <div class="acc-split-legend">
              <span><i class="acc-dot acc-dot--active"></i>Active <b>{{ accounts ? formatNumber(accounts.active) : '–' }}</b></span>
              <span><i class="acc-dot acc-dot--inactive"></i>Inactive <b>{{ accounts ? formatNumber(accounts.inactive) : '–' }}</b></span>
            </div>
            <router-link :to="applicantsPage.route" class="acc-applicants">
              <span class="acc-role-icon" :style="{ '--role': applicantsPage.color, '--role-soft': applicantsPage.soft }">
                <v-icon size="20">{{ applicantsPage.icon }}</v-icon>
              </span>
              <span class="acc-applicants-text">
                <span class="acc-applicants-value">{{ accounts ? formatNumber(accounts[applicantsPage.stat]) : '–' }}</span>
                <span class="acc-applicants-label">Applicant accounts</span>
              </span>
              <v-icon size="18" class="acc-go">mdi-arrow-right</v-icon>
            </router-link>
          </div>

          <ul class="acc-roles">
            <li v-for="r in officialPages" :key="r.key">
              <router-link :to="r.route" class="acc-role">
                <span class="acc-role-icon" :style="{ '--role': r.color, '--role-soft': r.soft }">
                  <v-icon size="18">{{ r.icon }}</v-icon>
                </span>
                <span class="acc-role-name">{{ r.name }}</span>
                <span class="acc-role-bar">
                  <span :style="{ width: (accounts ? roleShare(r) : 0) + '%' }"></span>
                </span>
                <span class="acc-role-count">{{ accounts ? formatNumber(accounts[r.stat]) : '–' }}</span>
              </router-link>
            </li>
          </ul>
        </div>
      </section>
    </div>

    <!-- Bulk delete confirmation (super admin) -->
    <v-dialog v-model="confirmDialog" max-width="480" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head dialog-head--danger">
          <div class="dialog-head-title">Delete {{ selectedCount === 1 ? "1 application" : selectedCount + " applications" }}</div>
          <div class="dialog-head-subtitle">This cannot be undone</div>
        </div>
        <div class="pa-5">
          Delete {{ selectedCount === 1 ? "1 application" : selectedCount + " applications" }} (<b>{{ selectedPreview }}</b>) completely, along with their
          forwarding history and payment records?
        </div>
        <v-card-actions class="px-5 pb-5">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" :disabled="deleting" @click="confirmDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--danger-solid" :loading="deleting" @click="bulkDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackBar" top right :timeout="4000">
      {{ msg }}
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="showSnackBar = false">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import CountUp from "@/components/Common/CountUp";
import { stagesFor, statusOf, TASK_LABELS, desks } from "@/components/Common/deskConfig";
import { canBulkDeleteApps, getUserType, isSuperAdmin, panelLabels } from "@/components/Common/navConfig";
import { USER_PAGES, userPage } from "@/components/superAdmin/userRoles";

// Per-role wording and links. `listRoute` is the full application list the
// status tiles open (with ?status=), when the role has one.
const ROLES = {
  admin: {
    subtitle: "Film shooting applications across every circle and division.",
    scopeLabel: "state",
    listRoute: "/admin-applications",
    detailRoute: "/AdminFilmDetailedView",
  },
  division: {
    subtitle: "Film shooting applications in your division.",
    scopeLabel: "division",
    listRoute: "/division-applications",
    detailRoute: "/AdminFilmDetailedView",
  },
  clerk: {
    subtitle: "Applications in your division and what is waiting at the Section Clerk desk.",
    scopeLabel: "division",
    listRoute: null,
    detailRoute: "/CampDetailedViewClerk",
  },
  superintendent: {
    subtitle: "Applications in your division and what is waiting at the Superintendent desk.",
    scopeLabel: "division",
    listRoute: null,
    detailRoute: "/DetailedViewsuperintedent",
  },
  range: {
    subtitle: "Applications sent to your range office for site inspection.",
    scopeLabel: "range",
    listRoute: null,
    detailRoute: "/CampDetailedView",
  },
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Status colours (checked for colour-blind separation; amber is below 3:1 on
// white, so every value is also written out in the legend and tiles).
const STATUS = [
  { key: "approved", label: "Approved / paid", status: "Success", icon: "mdi-check-decagram-outline", color: "#15803d", soft: "#dcfce7" },
  { key: "pending", label: "Under review", status: "Pending", icon: "mdi-clock-outline", color: "#f59e0b", soft: "#fef3c7" },
  { key: "rejected", label: "Rejected", status: "rejected", icon: "mdi-close-octagon-outline", color: "#dc2626", soft: "#fee2e2" },
  { key: "cancelled", label: "Cancelled", status: "Cancelled", icon: "mdi-cancel", color: "#6366f1", soft: "#e0e7ff" },
];

// Status filter of the monthly chart, insights and latest applications.
// `count` is the matching field of the summary's counts.
const STATUS_FILTERS = [
  { key: "all", label: "All", icon: "mdi-view-grid-outline", count: "total" },
  { key: "pending", label: "Under review", icon: "mdi-clock-outline", count: "pending" },
  { key: "approved", label: "Approved / paid", icon: "mdi-check-decagram-outline", count: "approved" },
  { key: "rejected", label: "Rejected", icon: "mdi-close-octagon-outline", count: "rejected" },
  { key: "cancelled", label: "Cancelled", icon: "mdi-cancel", count: "cancelled" },
  { key: "paid", label: "Paid", icon: "mdi-cash-check", count: "paid" },
  { key: "refunded", label: "Refunded", icon: "mdi-cash-refund", count: "refunded" },
];

const EMPTY_COUNTS = { total: 0, pending: 0, approved: 0, rejected: 0, cancelled: 0, paid: 0, refunded: 0 };

const zeros = () => new Array(12).fill(0);

// Months of a summary response, or zeros when it failed
const monthsOf = (res) => (res && res.data && res.data.status && res.data.data.monthly ? res.data.data.monthly : zeros());

const compactNumber = new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 });

const BRAND = "#15803d";
const REFUND = "#2563eb";
const PREVIOUS = "#64748b";
const GRID = "#eef2f0";
const MUTED = "#64748b";
const INK = "#0f172a";

function prefersReducedMotion() {
  return typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function chartAnimations() {
  return prefersReducedMotion()
    ? { enabled: false }
    : { enabled: true, easing: "easeinout", speed: 900, animateGradually: { enabled: true, delay: 90 }, dynamicAnimation: { enabled: true, speed: 450 } };
}

const sum = (list) => list.reduce((a, b) => a + (Number(b) || 0), 0);

export default {
  name: "RoleDashboard",
  components: { CountUp },
  props: {
    role: { type: String, required: true },
  },
  data() {
    const thisYear = new Date().getFullYear();
    return {
      loaded: false,
      refreshing: false,
      requestSeq: 0,
      filterSeq: 0,
      year: thisYear,
      years: [thisYear, thisYear - 1, thisYear - 2, thisYear - 3],
      months: MONTHS,
      trendView: "chart",
      counts: Object.assign({}, EMPTY_COUNTS),
      desk: [],
      // All applications: this year's and last year's months, and the latest
      baseMonthly: zeros(),
      basePrevMonthly: zeros(),
      baseRecent: [],
      // The same for the chosen status (null until it has loaded)
      filtered: null,
      statusFilter: "all",
      statusFilters: STATUS_FILTERS,
      moneyView: "chart",
      // Status filters and fees / refunds come from the newer summary API
      extras: false,
      money: null,
      accounts: null,
      superAdmin: isSuperAdmin(),
      canDeleteApps: canBulkDeleteApps(),
      // Bulk delete of the latest applications (super admin): id -> application number
      selectMode: false,
      selected: {},
      confirmDialog: false,
      deleting: false,
      msg: "",
      showSnackBar: false,
      usersRoute: userPage("all").route,
      applicantsPage: userPage("772"),
      officialPages: USER_PAGES.filter((p) => p.key !== "all" && p.key !== "772"),
    };
  },
  computed: {
    config() {
      return ROLES[this.role] || ROLES.admin;
    },
    panelLabel() {
      return panelLabels[getUserType()] || "Dashboard";
    },
    deskRoute() {
      return desks[this.role] ? desks[this.role].listRoute : null;
    },
    deskStages() {
      return stagesFor(this.role).map((s) => {
        const found = this.desk.find((d) => d.task === s.task);
        return { ...s, count: found ? found.count : 0 };
      });
    },
    deskTotal() {
      return sum(this.deskStages.map((s) => s.count));
    },
    statusTiles() {
      const to = (status) => (this.config.listRoute ? { path: this.config.listRoute, query: { status } } : null);
      return STATUS.map((s) => ({ ...s, to: to(s.status) }));
    },
    statusSum() {
      return sum(STATUS.map((s) => this.counts[s.key]));
    },
    userName() {
      return localStorage.getItem("username") || "there";
    },
    greeting() {
      const h = new Date().getHours();
      if (h < 12) return "Good morning";
      if (h < 17) return "Good afternoon";
      return "Good evening";
    },
    today() {
      return moment().format("dddd, D MMMM YYYY");
    },
    isCurrentYear() {
      return this.year === new Date().getFullYear();
    },
    // Months of the selected year that have started (all 12 for a past year)
    monthsElapsed() {
      return this.isCurrentYear ? new Date().getMonth() + 1 : 12;
    },
    // Months and latest applications of the chosen status (all by default)
    filterActive() {
      return this.statusFilter !== "all" && !!this.filtered;
    },
    monthly() {
      return this.filterActive ? this.filtered.monthly : this.baseMonthly;
    },
    prevMonthly() {
      return this.filterActive ? this.filtered.prevMonthly : this.basePrevMonthly;
    },
    recent() {
      return this.filterActive ? this.filtered.recent : this.baseRecent;
    },
    statusFilterLabel() {
      const f = STATUS_FILTERS.find((s) => s.key === this.statusFilter);
      return f ? f.label : "";
    },
    yearTotal() {
      return sum(this.monthly);
    },
    prevYearTotal() {
      return sum(this.prevMonthly);
    },
    baseYearTotal() {
      return sum(this.baseMonthly);
    },
    // This year against the same months of last year (all applications)
    yearDelta() {
      const n = this.monthsElapsed;
      const now = sum(this.baseMonthly.slice(0, n));
      const before = sum(this.basePrevMonthly.slice(0, n));
      if (!this.loaded || !before) return { pct: null, tone: "flat", note: "" };
      const pct = Math.round(((now - before) / before) * 100);
      return {
        pct,
        tone: pct > 0 ? "up" : pct < 0 ? "down" : "flat",
        note: this.isCurrentYear ? "vs same months of " + (this.year - 1) : "vs " + (this.year - 1),
      };
    },
    approvalRate() {
      return this.counts.total ? Math.round((this.counts.approved / this.counts.total) * 100) : 0;
    },
    insights() {
      const peak = Math.max(...this.monthly);
      const peakMonth = peak > 0 ? MONTHS[this.monthly.indexOf(peak)] : "—";
      const thisMonth = this.isCurrentYear ? this.monthly[new Date().getMonth()] : null;
      const average = this.yearTotal / this.monthsElapsed;
      const rows = [
        { label: "Received in " + this.year, value: this.formatNumber(this.yearTotal), icon: "mdi-inbox-arrow-down-outline" },
        { label: "Busiest month", value: peak > 0 ? peakMonth + " · " + peak : "—", icon: "mdi-calendar-star" },
        { label: "Monthly average", value: average.toLocaleString("en-IN", { maximumFractionDigits: 1 }), icon: "mdi-chart-bell-curve-cumulative" },
        { label: "Rejection rate", value: this.share(this.counts.rejected) + "%", icon: "mdi-close-octagon-outline" },
      ];
      if (thisMonth !== null) rows.splice(1, 0, { label: "This month", value: this.formatNumber(thisMonth), icon: "mdi-calendar-today" });
      return rows;
    },
    selectedCount() {
      return Object.keys(this.selected).length;
    },
    selectedPreview() {
      const numbers = Object.values(this.selected);
      const more = numbers.length > 5 ? ` and ${numbers.length - 5} more` : "";
      return numbers.slice(0, 5).map((n) => "#" + n).join(", ") + more;
    },
    allSelected() {
      return this.recent.length > 0 && this.recent.every((app) => this.isSelected(app));
    },
    recentItems() {
      return this.recent.map((app) => ({
        ...app,
        to: this.config.detailRoute ? { path: this.config.detailRoute, query: { id: app._id } } : null,
      }));
    },
    activeShare() {
      const a = this.accounts;
      return a && a.total ? Math.round((a.active / a.total) * 100) : 0;
    },
    maxOfficial() {
      if (!this.accounts) return 0;
      return Math.max(1, ...this.officialPages.map((p) => this.accounts[p.stat] || 0));
    },

    // ── Fees and refunds ──
    feesThisYear() {
      return this.money ? sum(this.money.payments.monthly) : 0;
    },
    refundsThisYear() {
      return this.money ? sum(this.money.refunds.monthly) : 0;
    },
    netKept() {
      return this.money ? Math.max(this.money.payments.amount - this.money.refunds.completed.amount, 0) : 0;
    },
    // Share of all fees received that was paid back, in whole percent
    refundShare() {
      if (!this.money || !this.money.payments.amount) return 0;
      return Math.min(100, Math.round((this.money.refunds.completed.amount / this.money.payments.amount) * 100));
    },
    moneyTiles() {
      const m = this.money;
      if (!m) return [];
      return [
        { key: "fees", label: "Fees received", value: m.payments.amount, money: true, note: this.formatNumber(m.payments.count) + " paid applications", icon: "mdi-cash-check", color: BRAND, soft: "#dcfce7" },
        { key: "year", label: "Fees in " + this.year, value: this.feesThisYear, money: true, note: "Received this year", icon: "mdi-calendar-cash-outline", color: "#0f766e", soft: "#ccfbf1" },
        { key: "refunded", label: "Refunded", value: m.refunds.completed.amount, money: true, note: this.formatNumber(m.refunds.completed.count) + " refunds paid", icon: "mdi-cash-refund", color: REFUND, soft: "#dbeafe" },
        { key: "progress", label: "Refunds in progress", value: m.refunds.inProgress.count, money: false, note: this.rupees(m.refunds.inProgress.amount) + " to pay", icon: "mdi-progress-clock", color: "#d97706", soft: "#fef3c7" },
      ];
    },
    refundRows() {
      const r = this.money ? this.money.refunds : null;
      if (!r) return [];
      return [
        { key: "completed", label: "Refunded", count: r.completed.count, amount: r.completed.amount, icon: "mdi-check-circle-outline", color: "#15803d", soft: "#dcfce7" },
        { key: "inProgress", label: "In progress", count: r.inProgress.count, amount: r.inProgress.amount, icon: "mdi-progress-clock", color: "#d97706", soft: "#fef3c7" },
        { key: "failed", label: "Failed", count: r.failed.count, amount: r.failed.amount, icon: "mdi-alert-circle-outline", color: "#dc2626", soft: "#fee2e2" },
      ];
    },
    moneySeries() {
      if (!this.money) return [];
      return [
        { name: "Fees received", data: this.money.payments.monthly },
        { name: "Refunds paid", data: this.money.refunds.monthly },
      ];
    },
    moneyOptions() {
      return {
        chart: { type: "bar", toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit", animations: chartAnimations() },
        colors: [BRAND, REFUND],
        plotOptions: { bar: { columnWidth: "56%", borderRadius: 4, borderRadiusApplication: "end" } },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        dataLabels: { enabled: false },
        grid: { borderColor: GRID, strokeDashArray: 0, padding: { left: 6, right: 10 } },
        xaxis: {
          categories: MONTHS,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: MUTED, fontSize: "12px" } },
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: { style: { colors: MUTED, fontSize: "12px" }, formatter: (v) => "₹" + compactNumber.format(v) },
        },
        legend: {
          show: true,
          position: "top",
          horizontalAlign: "right",
          fontSize: "13px",
          labels: { colors: INK },
          markers: { width: 10, height: 10, radius: 10 },
          itemMargin: { horizontal: 10 },
        },
        tooltip: { shared: true, intersect: false, y: { formatter: (v) => this.rupees(v) } },
      };
    },

    // ── Charts ──
    sparkSeries() {
      return [{ name: "Applications", data: this.baseMonthly }];
    },
    sparkOptions() {
      return {
        chart: { sparkline: { enabled: true }, animations: chartAnimations(), fontFamily: "inherit" },
        colors: ["#bbf7d0"],
        stroke: { width: 2, curve: "smooth", lineCap: "round" },
        fill: { type: "gradient", gradient: { shadeIntensity: 0, opacityFrom: 0.35, opacityTo: 0, stops: [0, 100] } },
        xaxis: { categories: MONTHS },
        tooltip: {
          theme: "dark",
          x: { show: true },
          y: { title: { formatter: () => "" }, formatter: (v) => v + " application" + (v === 1 ? "" : "s") },
          marker: { show: false },
        },
      };
    },
    trendSeries() {
      return [
        { name: String(this.year), type: "area", data: this.monthly },
        { name: String(this.year - 1), type: "line", data: this.prevMonthly },
      ];
    },
    trendOptions() {
      return {
        chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit", animations: chartAnimations() },
        colors: [BRAND, PREVIOUS],
        stroke: { width: [2, 2], curve: "smooth", lineCap: "round" },
        fill: {
          type: ["gradient", "solid"],
          gradient: { shadeIntensity: 0, opacityFrom: 0.2, opacityTo: 0.02, stops: [0, 100] },
          opacity: [1, 1],
        },
        markers: { size: 0, strokeColors: "#ffffff", strokeWidth: 2, hover: { size: 6 } },
        dataLabels: { enabled: false },
        grid: { borderColor: GRID, strokeDashArray: 0, padding: { left: 6, right: 10 } },
        xaxis: {
          categories: MONTHS,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: MUTED, fontSize: "12px" } },
          crosshairs: { stroke: { color: "#cbd5e1", width: 1, dashArray: 0 } },
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: { style: { colors: MUTED, fontSize: "12px" }, formatter: (v) => Math.round(v) },
        },
        legend: {
          show: true,
          position: "top",
          horizontalAlign: "right",
          fontSize: "13px",
          labels: { colors: INK },
          markers: { width: 10, height: 10, radius: 10 },
          itemMargin: { horizontal: 10 },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: { formatter: (v) => (v == null ? "—" : v + " application" + (v === 1 ? "" : "s")) },
        },
      };
    },
    donutSeries() {
      return STATUS.map((s) => this.counts[s.key] || 0);
    },
    donutOptions() {
      const total = this.counts.total;
      return {
        chart: { type: "donut", fontFamily: "inherit", animations: chartAnimations() },
        labels: STATUS.map((s) => s.label),
        colors: STATUS.map((s) => s.color),
        stroke: { width: 2, colors: ["#ffffff"] },
        dataLabels: { enabled: false },
        legend: { show: false },
        states: { hover: { filter: { type: "darken", value: 0.88 } }, active: { filter: { type: "none" } } },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              size: "74%",
              labels: {
                show: true,
                name: { show: true, offsetY: 22, color: MUTED, fontSize: "12.5px", fontWeight: 600 },
                value: { show: true, offsetY: -12, color: INK, fontSize: "28px", fontWeight: 800, formatter: (v) => Number(v).toLocaleString("en-IN") },
                total: { show: true, showAlways: false, label: "Applications", color: MUTED, fontSize: "12.5px", fontWeight: 600, formatter: () => Number(total).toLocaleString("en-IN") },
              },
            },
          },
        },
        tooltip: { y: { formatter: (v) => v + " (" + this.share(v) + "%)" } },
      };
    },
    gaugeOptions() {
      return {
        chart: { type: "radialBar", sparkline: { enabled: true }, fontFamily: "inherit", animations: chartAnimations() },
        colors: [BRAND],
        labels: ["Approval rate"],
        stroke: { lineCap: "round" },
        plotOptions: {
          radialBar: {
            startAngle: -120,
            endAngle: 120,
            hollow: { size: "64%" },
            track: { background: "#dcfce7", strokeWidth: "100%", margin: 0 },
            dataLabels: {
              name: { show: true, offsetY: 24, color: MUTED, fontSize: "12.5px", fontWeight: 600 },
              value: { show: true, offsetY: -12, color: INK, fontSize: "28px", fontWeight: 800, formatter: (v) => Math.round(v) + "%" },
            },
          },
        },
      };
    },
  },
  watch: {
    year() {
      this.load();
    },
    // The listed applications change with the status filter: start the selection over
    statusFilter() {
      this.clearSelection();
    },
  },
  mounted() {
    this.load();
    if (this.superAdmin) this.loadAccounts();
  },
  methods: {
    statusOf,
    formatNumber(n) {
      return Number(n || 0).toLocaleString("en-IN");
    },
    // Share of all applications, in whole percent
    share(n) {
      return this.counts.total ? Math.round(((Number(n) || 0) / this.counts.total) * 100) : 0;
    },
    roleShare(page) {
      return Math.round(((this.accounts[page.stat] || 0) / this.maxOfficial) * 100);
    },
    signed(n) {
      return n > 0 ? "+" + n : String(n);
    },
    changeClass(n) {
      return n > 0 ? "num-up" : n < 0 ? "num-down" : "";
    },
    // ---------- bulk select / delete (latest applications) ----------
    toast(text) {
      this.msg = text;
      this.showSnackBar = true;
    },
    toggleSelectMode() {
      this.selectMode = !this.selectMode;
      if (!this.selectMode) this.clearSelection();
    },
    isSelected(app) {
      return Object.prototype.hasOwnProperty.call(this.selected, app._id);
    },
    toggleItem(app) {
      if (this.isSelected(app)) this.$delete(this.selected, app._id);
      else this.$set(this.selected, app._id, app.applicationNo || app._id);
    },
    toggleAll(value) {
      this.recent.forEach((app) => {
        if (value) this.$set(this.selected, app._id, app.applicationNo || app._id);
        else this.$delete(this.selected, app._id);
      });
    },
    clearSelection() {
      this.selected = {};
    },
    bulkDelete() {
      const ids = Object.keys(this.selected);
      if (!ids.length) return;
      this.deleting = true;
      axios
        .post("/superadmin/delete/applications", { appids: ids }, { headers: { token: localStorage.getItem("token") } })
        .then((response) => {
          this.deleting = false;
          const data = response.data || {};
          this.toast(data.msg || (data.status ? "Applications deleted." : "Could not delete the applications."));
          if (data.status) {
            this.confirmDialog = false;
            this.clearSelection();
            this.selectMode = false;
            this.load();
          }
        })
        .catch((err) => {
          this.deleting = false;
          this.toast("Couldn't delete the selected applications. Please try again.");
          console.log(err);
        });
    },
    ago(date) {
      return date ? moment(date).fromNow() : "";
    },
    taskLabel(app) {
      if (app.status !== "Active") return app.status;
      return TASK_LABELS[app.task] || app.task;
    },
    rupees(n) {
      return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
    },
    // `params`: { year, status?, listOnly? } (listOnly: just the months and
    // the latest applications)
    summary(params) {
      return axios({
        method: "POST",
        url: "/dashboard/summary",
        headers: { token: localStorage.getItem("token") },
        data: params,
      });
    },
    // The selected year's summary and last year's months, for the comparison,
    // then the chosen status if there is one. A refetch keeps the current
    // charts (dimmed) instead of flashing skeletons.
    load() {
      const seq = ++this.requestSeq;
      if (this.loaded) this.refreshing = true;
      const year = this.year;
      Promise.all([this.summary({ year }), this.summary({ year: year - 1, listOnly: true }).catch(() => null)])
        .then(([current, previous]) => {
          if (seq !== this.requestSeq) return;
          if (current.data.status) {
            const data = current.data.data;
            this.counts = Object.assign({}, EMPTY_COUNTS, data.counts);
            this.desk = data.desk || [];
            this.baseMonthly = data.monthly || zeros();
            this.baseRecent = data.recent || [];
            this.extras = data.statusFilter !== undefined;
            this.money = data.payments && data.refunds ? { payments: data.payments, refunds: data.refunds } : null;
          }
          this.basePrevMonthly = monthsOf(previous);
          this.loaded = true;
          if (this.statusFilter !== "all" && this.extras) {
            this.loadFiltered();
          } else {
            this.refreshing = false;
          }
        })
        .catch((err) => {
          if (seq !== this.requestSeq) return;
          this.loaded = true;
          this.refreshing = false;
          console.log(err);
        });
    },
    // Months (this year and last) and latest applications of the chosen status
    loadFiltered() {
      const seq = ++this.filterSeq;
      const status = this.statusFilter;
      const year = this.year;
      this.refreshing = true;
      Promise.all([
        this.summary({ year, status, listOnly: true }),
        this.summary({ year: year - 1, status, listOnly: true }).catch(() => null),
      ])
        .then(([current, previous]) => {
          if (seq !== this.filterSeq) return;
          const data = current.data.status ? current.data.data : {};
          this.filtered = { monthly: data.monthly || zeros(), prevMonthly: monthsOf(previous), recent: data.recent || [] };
          this.refreshing = false;
        })
        .catch((err) => {
          if (seq !== this.filterSeq) return;
          this.refreshing = false;
          console.log(err);
        });
    },
    setStatusFilter(key) {
      if (this.statusFilter === key) return;
      this.statusFilter = key;
      this.filtered = null;
      if (key === "all") {
        // The overall figures are already loaded; drop any status request in flight
        this.filterSeq++;
        this.refreshing = false;
        return;
      }
      this.loadFiltered();
    },
    loadAccounts() {
      axios({ method: "GET", url: "/superadmin/stats/users", headers: { token: localStorage.getItem("token") } })
        .then((res) => {
          if (res.data.status && res.data.data) this.accounts = res.data.data;
        })
        .catch((err) => console.log(err));
    },
  },
};
</script>

<style scoped>
.dash {
  max-width: 1600px;
  margin: 0 auto;
}

/* ---------- Entrance animation ---------- */
.dash-rise {
  animation: dash-rise 0.6s cubic-bezier(0.2, 0.7, 0.2, 1) both;
  animation-delay: calc(var(--i, 0) * 70ms);
}

@keyframes dash-rise {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* ---------- Hero ---------- */
.dash-hero {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px 24px;
  padding: 20px 24px 22px;
  margin-bottom: 20px;
  border-radius: 22px;
  color: #ffffff;
  background:
    radial-gradient(circle at 92% -10%, rgba(134, 239, 172, 0.28) 0, transparent 42%),
    linear-gradient(125deg, #052e16 0%, #14532d 55%, #15803d 100%);
  box-shadow: 0 18px 40px rgba(5, 46, 22, 0.22);
}

.dash-hero::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 6px;
  background: repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.3) 0 12px, transparent 12px 24px);
  animation: dash-film 6s linear infinite;
}

@keyframes dash-film {
  to {
    background-position: 240px 0;
  }
}

.dash-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(2px);
  pointer-events: none;
}

.dash-blob--one {
  top: -120px;
  right: 22%;
  width: 280px;
  height: 280px;
  background: radial-gradient(circle, rgba(74, 222, 128, 0.22) 0%, transparent 70%);
  animation: dash-float 14s ease-in-out infinite;
}

.dash-blob--two {
  bottom: -140px;
  left: -60px;
  width: 320px;
  height: 320px;
  background: radial-gradient(circle, rgba(250, 204, 21, 0.12) 0%, transparent 70%);
  animation: dash-float 18s ease-in-out infinite reverse;
}

@keyframes dash-float {
  0%,
  100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(30px, 20px);
  }
}

.dash-hero-main {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1 1 420px;
  min-width: 0;
}

.dash-hero-kicker {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #bbf7d0;
}

.dash-hero-kicker .v-icon {
  color: inherit !important;
}

.dash-role-chip {
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.dash-hero-title {
  margin: 6px 0 2px;
  font-size: clamp(20px, 2.2vw, 26px);
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.3px;
}

.dash-hero-sub {
  margin: 0;
  max-width: 560px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
}

.dash-hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.dash-hero-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 0.15s ease, transform 0.15s ease;
}

.dash-hero-btn .v-icon {
  color: inherit !important;
}

.dash-hero-btn:hover {
  background: rgba(255, 255, 255, 0.18);
  transform: translateY(-1px);
}

.dash-hero-btn:focus-visible {
  outline: 2px solid #bbf7d0;
  outline-offset: 2px;
}

.dash-hero-btn--solid {
  border-color: #ffffff;
  background: #ffffff;
  color: #14532d;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
}

.dash-hero-btn--solid:hover {
  background: #f0fdf4;
}

.dash-hero-btn-badge {
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: #f59e0b;
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
  line-height: 22px;
  text-align: center;
}

.dash-hero-figure {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: auto minmax(130px, 1fr);
  align-items: center;
  gap: 16px;
  flex: 0 1 460px;
  min-width: 300px;
  padding: 12px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(6px);
}

.dash-hero-figure-text {
  min-width: 0;
}

.dash-hero-figure-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
}

.dash-reel {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px dashed rgba(255, 255, 255, 0.4);
  animation: dash-spin 14s linear infinite;
}

.dash-reel .v-icon {
  color: #ffffff !important;
}

@keyframes dash-spin {
  to {
    transform: rotate(360deg);
  }
}

.dash-hero-figure-value {
  margin-top: 4px;
  font-size: 40px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.8px;
}

.dash-hero-delta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 6px;
  margin-top: 6px;
  max-width: 220px;
  font-size: 13px;
  font-weight: 800;
}

.dash-hero-delta .v-icon {
  color: inherit !important;
}

.dash-hero-delta--up {
  color: #86efac;
}

.dash-hero-delta--down {
  color: #fca5a5;
}

.dash-hero-delta--flat {
  color: rgba(255, 255, 255, 0.85);
}

.dash-hero-delta-note {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
}

.dash-hero-spark {
  min-width: 0;
}

.dash-hero-spark >>> .apexcharts-canvas {
  margin: 0 auto;
}

.dash-hero-figure-foot {
  margin-top: 2px;
  font-size: 11px;
  text-align: right;
  color: rgba(255, 255, 255, 0.6);
}

/* ---------- Status tiles ---------- */
.dash-tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.dash-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 18px 18px 16px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: #ffffff;
  color: inherit;
  text-decoration: none;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.dash-tile::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--tone);
}

a.dash-tile:hover {
  transform: translateY(-3px);
  border-color: var(--tone);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.09);
}

a.dash-tile:focus-visible {
  outline: 2px solid var(--tone);
  outline-offset: 2px;
}

.dash-tile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.dash-tile-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: var(--tone-soft);
  transition: background-color 0.2s ease;
}

.dash-tile-icon .v-icon {
  color: var(--tone) !important;
  transition: color 0.2s ease;
}

a.dash-tile:hover .dash-tile-icon {
  background: var(--tone);
}

a.dash-tile:hover .dash-tile-icon .v-icon {
  color: #ffffff !important;
}

.dash-tile-share {
  padding: 3px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
  font-size: 12px;
  font-weight: 800;
}

.dash-tile-value {
  font-size: 30px;
  font-weight: 800;
  line-height: 1.1;
  color: var(--ink);
}

.dash-tile-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-muted);
}

.dash-tile-go {
  position: absolute;
  right: 16px;
  bottom: 30px;
  color: #cbd5e1 !important;
  transition: transform 0.2s ease, color 0.2s ease;
}

a.dash-tile:hover .dash-tile-go {
  color: var(--tone) !important;
  transform: translateX(3px);
}

.dash-meter {
  display: block;
  height: 6px;
  margin-top: 10px;
  border-radius: 999px;
  background: var(--tone-soft, #e8f4ec);
  overflow: hidden;
}

.dash-meter-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--tone, #15803d);
  transition: width 1.1s cubic-bezier(0.2, 0.7, 0.2, 1);
}

/* ---------- Cards grid ---------- */
.dash-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

.dash-span-2 {
  grid-column: span 2;
}

.dash-span-3 {
  grid-column: 1 / -1;
}

.dash-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.dash-card-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 12px;
}

.dash-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 800;
  color: var(--ink);
}

.dash-card-sub {
  margin-top: 2px;
  font-size: 13px;
  color: var(--ink-muted);
}

.dash-card-tools {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dash-card-body {
  flex: 1;
  padding: 0 16px 16px;
  transition: opacity 0.2s ease;
}

.dash-refreshing {
  opacity: 0.5;
}

.dash-badge {
  min-width: 26px;
  padding: 2px 9px;
  border-radius: 999px;
  background: #fef3c7;
  color: #92400e;
  font-size: 12.5px;
  font-weight: 800;
  text-align: center;
}

.dash-year {
  max-width: 110px;
}

.dash-switch {
  display: flex;
  gap: 2px;
  padding: 3px;
  border-radius: 10px;
  background: #f1f5f2;
  border: 1px solid var(--line);
}

.dash-switch-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.dash-switch-btn .v-icon {
  color: inherit !important;
}

.dash-switch-btn--on {
  background: #ffffff;
  color: var(--brand-dark);
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.1);
}

.dash-switch-btn:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 1px;
}

/* Table view of the monthly chart */
.dash-table-wrap {
  max-height: 310px;
  overflow-y: auto;
  border: 1px solid var(--line);
  border-radius: 12px;
}

.dash-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
}

.dash-table th,
.dash-table td {
  padding: 8px 14px;
  border-bottom: 1px solid #f0f4f5;
  text-align: left;
}

.dash-table thead th {
  position: sticky;
  top: 0;
  background: #f3faf5;
  color: var(--brand-dark);
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.dash-table tfoot td {
  font-weight: 800;
  background: #fbfdfb;
}

.dash-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.num-up {
  color: #15803d;
}

.num-down {
  color: #b91c1c;
}

/* Donut legend (also the table view of the donut) */
.dash-legend {
  display: grid;
  gap: 4px;
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
}

.dash-legend li {
  display: grid;
  grid-template-columns: 12px minmax(0, 1fr) auto 44px;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 10px;
  font-size: 13.5px;
  transition: background-color 0.15s ease;
}

.dash-legend li:hover {
  background: #f7fbf8;
}

.dash-legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dash-legend-name {
  color: #334155;
  font-weight: 600;
}

.dash-legend-value {
  font-weight: 800;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.dash-legend-pct {
  color: var(--ink-muted);
  font-size: 12.5px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* Insights */
.dash-insights {
  display: grid;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.dash-insights li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 6px;
  border-bottom: 1px dashed #e7efe9;
}

.dash-insights li:last-child {
  border-bottom: none;
}

.dash-insight-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 10px;
  background: var(--brand-soft);
}

.dash-insight-icon .v-icon {
  color: var(--brand) !important;
}

.dash-insight-label {
  flex: 1;
  font-size: 13.5px;
  color: #334155;
}

.dash-insight-value {
  font-size: 14px;
  font-weight: 800;
  color: var(--ink);
}

/* ---------- Filter row ---------- */
.dash-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}

.dash-filters-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.dash-filters-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.dash-filters-label .v-icon {
  color: var(--brand) !important;
}

.dash-filters-note {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--ink);
}

.dash-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.dash-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid #dbe5df;
  background: #ffffff;
  color: #334155;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.dash-chip .v-icon {
  color: inherit !important;
}

.dash-chip:hover {
  border-color: var(--brand);
  color: var(--brand-dark);
}

.dash-chip:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 2px;
}

.dash-chip--on,
.dash-chip--on:hover {
  border-color: transparent;
  background: linear-gradient(135deg, #15803d 0%, #14532d 100%);
  color: #ffffff;
  box-shadow: 0 6px 14px rgba(21, 128, 61, 0.25);
}

.dash-chip-count {
  min-width: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
  font-size: 11.5px;
  line-height: 20px;
  text-align: center;
}

.dash-chip--on .dash-chip-count {
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

.dash-filters-side {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dash-filters-hint {
  max-width: 240px;
  font-size: 12px;
  line-height: 1.35;
  color: var(--ink-muted);
}

.dash-filters .dash-year {
  max-width: 140px;
}

.dash-filter-tag {
  padding: 2px 10px;
  border-radius: 999px;
  background: var(--brand-soft);
  color: var(--brand-dark);
  font-size: 12px;
  font-weight: 800;
}

/* ---------- Fees and refunds ---------- */
.money-tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 0 20px 8px;
}

.money-tile {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, var(--tone-soft) 0%, #ffffff 70%);
}

.money-tile-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: var(--tone);
}

.money-tile-icon .v-icon {
  color: #ffffff !important;
}

.money-tile-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.money-tile-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-muted);
}

.money-tile-value {
  font-size: 19px;
  font-weight: 800;
  line-height: 1.25;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.money-tile-note {
  font-size: 11.5px;
  color: var(--ink-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.net-value {
  font-size: 34px;
  font-weight: 800;
  line-height: 1.1;
  color: var(--ink);
}

.net-bar {
  display: flex;
  gap: 2px;
  height: 12px;
  margin-top: 12px;
  border-radius: 999px;
  overflow: hidden;
  background: #ffffff;
}

.net-bar-part {
  flex-basis: 0;
  min-width: 4px;
  animation: net-grow 1.1s cubic-bezier(0.2, 0.7, 0.2, 1) both;
  transform-origin: left center;
}

.net-bar-part--kept {
  background: #15803d;
}

.net-bar-part--refunded {
  background: #2563eb;
}

@keyframes net-grow {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

.net-legend {
  display: grid;
  gap: 2px;
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
}

.net-legend li {
  display: grid;
  grid-template-columns: 12px minmax(0, 1fr) auto 44px;
  align-items: center;
  gap: 10px;
  padding: 6px 4px;
  font-size: 13.5px;
}

.net-legend-name {
  color: #334155;
  font-weight: 600;
}

.net-legend b {
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.net-legend-pct {
  font-size: 12.5px;
  color: var(--ink-muted);
  text-align: right;
}

.net-subtitle {
  margin: 14px 0 6px;
  padding-top: 12px;
  border-top: 1px dashed #e7efe9;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.refund-list {
  display: grid;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.refund-list li {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 12px;
  background: #fbfdfb;
  border: 1px solid #eef3ef;
}

.refund-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: var(--tone-soft);
}

.refund-icon .v-icon {
  color: var(--tone) !important;
}

.refund-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #334155;
}

.refund-count {
  min-width: 28px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #f1f5f9;
  font-size: 12.5px;
  font-weight: 800;
  text-align: center;
  color: var(--ink);
}

.refund-amount {
  min-width: 80px;
  font-size: 13.5px;
  font-weight: 800;
  text-align: right;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

/* ---------- Desk ---------- */
.desk-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  padding: 4px 20px 20px;
}

.desk-grid--1 {
  grid-template-columns: minmax(0, 1fr);
}

.desk-card {
  --tone: #15803d;
  --tone-soft: #dcfce7;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: #fbfdfb;
  color: inherit;
  text-decoration: none;
  animation: dash-rise 0.6s cubic-bezier(0.2, 0.7, 0.2, 1) both;
  animation-delay: calc(0.5s + var(--i, 0) * 90ms);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.desk-card:hover {
  transform: translateY(-3px);
  border-color: rgba(21, 128, 61, 0.45);
  box-shadow: 0 14px 28px rgba(21, 128, 61, 0.14);
}

.desk-card:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 2px;
}

.desk-card-slate {
  display: block;
}

.desk-card-stripes {
  display: block;
  height: 10px;
  background: repeating-linear-gradient(115deg, #14532d 0 12px, #ffffff 12px 24px);
  background-size: 200% 100%;
  transition: background-position 0.6s ease;
}

.desk-card:hover .desk-card-stripes {
  background-position: 48px 0;
}

.desk-card-scene {
  display: block;
  padding: 6px 14px;
  background: #052e16;
  color: #bbf7d0;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.2px;
}

.desk-card-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 14px 4px;
}

.desk-card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #dcfce7;
}

.desk-card-icon .v-icon {
  color: #15803d !important;
}

.desk-card-count {
  font-size: 34px;
  font-weight: 800;
  line-height: 1;
  color: var(--ink);
}

.desk-card-name {
  padding: 4px 14px 0;
  font-size: 15px;
  font-weight: 800;
  color: var(--ink);
}

.desk-card-hint {
  display: -webkit-box;
  padding: 4px 14px 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--ink-muted);
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.desk-card-foot {
  display: block;
  margin-top: auto;
  padding: 4px 14px 14px;
}

.desk-card-share {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-muted);
}

/* ---------- Latest applications ---------- */
.recent-list {
  display: grid;
  gap: 2px;
  margin: 0;
  padding: 0 10px 12px;
  list-style: none;
}

.recent-list--grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 12px;
}

.recent-head-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.recent-item--select {
  cursor: pointer;
}

.recent-item--select:hover {
  background: #f8fafc;
}

.recent-item--selected,
.recent-item--selected:hover {
  background: #fef2f2;
}

.recent-check {
  flex-shrink: 0;
}

/* ---------- Bulk select ---------- */
.bulk-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin: 0 16px 12px;
  padding: 10px 12px 10px 14px;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.bulk-bar--active {
  border-style: solid;
  border-color: #fecaca;
  background: #fef2f2;
}

.bulk-bar-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  min-width: 0;
}

.bulk-bar-count {
  font-size: 14px;
  font-weight: 800;
  color: #7f1d1d;
}

.bulk-bar-hint {
  font-size: 12px;
  color: #64748b;
}

.bulk-bar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

@media (max-width: 600px) {
  .bulk-bar {
    margin: 0 12px 12px;
  }

  .bulk-bar-actions {
    width: 100%;
  }

  .bulk-bar-actions .btn-act {
    flex: 1 1 auto;
  }
}

.recent-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  color: inherit;
  text-decoration: none;
  transition: background-color 0.15s ease, transform 0.15s ease;
}

a.recent-item:hover {
  background: #f3faf5;
  transform: translateX(2px);
}

a.recent-item:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 1px;
}

.recent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
}

.recent-icon .v-icon {
  color: #15803d !important;
}

.recent-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.recent-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.recent-sub {
  font-size: 12.5px;
  color: var(--ink-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.recent-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.recent-when {
  font-size: 11.5px;
  color: #94a3b8;
  white-space: nowrap;
}

/* ---------- User accounts (super admin) ---------- */
.acc-body {
  display: grid;
  grid-template-columns: minmax(240px, 320px) minmax(0, 1fr);
  gap: 24px;
  padding: 4px 20px 20px;
}

.acc-summary {
  display: flex;
  flex-direction: column;
  padding: 18px;
  border-radius: 16px;
  background: linear-gradient(160deg, #f0fdf4 0%, #ffffff 100%);
  border: 1px solid var(--line);
}

.acc-total-label {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.acc-total {
  margin-top: 2px;
  font-size: 40px;
  font-weight: 800;
  line-height: 1.1;
  color: var(--ink);
}

.acc-split {
  height: 8px;
  margin-top: 12px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.acc-split-active {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #15803d;
  transition: width 1.1s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.acc-split-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 8px;
  font-size: 13px;
  color: #334155;
}

.acc-split-legend b {
  color: var(--ink);
}

.acc-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  margin-right: 6px;
  border-radius: 50%;
}

.acc-dot--active {
  background: #15803d;
}

.acc-dot--inactive {
  background: #94a3b8;
}

.acc-applicants {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: auto;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: #ffffff;
  color: inherit;
  text-decoration: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.acc-summary .acc-applicants {
  margin-top: 16px;
}

.acc-applicants:hover {
  border-color: var(--brand);
  box-shadow: 0 8px 18px rgba(21, 128, 61, 0.1);
}

.acc-applicants-text {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.acc-applicants-value {
  font-size: 20px;
  font-weight: 800;
  color: var(--ink);
}

.acc-applicants-label {
  font-size: 12.5px;
  color: var(--ink-muted);
}

.acc-go {
  color: #94a3b8 !important;
}

.acc-roles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 20px;
  align-content: start;
  margin: 0;
  padding: 0;
  list-style: none;
}

.acc-role {
  display: grid;
  grid-template-columns: 34px minmax(0, 120px) minmax(0, 1fr) 44px;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 12px;
  color: inherit;
  text-decoration: none;
  transition: background-color 0.15s ease;
}

.acc-role:hover {
  background: #f7fbf8;
}

.acc-role:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 1px;
}

.acc-role-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: var(--role-soft);
}

.acc-role-icon .v-icon {
  color: var(--role) !important;
}

.acc-role-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.acc-role-bar {
  height: 8px;
  border-radius: 999px;
  background: #e8f4ec;
  overflow: hidden;
}

.acc-role-bar > span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #15803d;
  transition: width 1.1s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.acc-role-count {
  font-size: 14px;
  font-weight: 800;
  color: var(--ink);
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* ---------- Responsive ---------- */
@media (max-width: 1264px) {
  .dash-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dash-span-2,
  .dash-span-3 {
    grid-column: 1 / -1;
  }

  .acc-roles {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 960px) {
  .dash-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .money-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dash-filters-main {
    flex-wrap: wrap;
  }

  .dash-filters-hint {
    display: none;
  }

  .dash-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .desk-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .recent-list--grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .acc-body {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 600px) {
  .dash-hero {
    padding: 20px 16px;
    border-radius: 20px;
    gap: 18px;
  }

  .dash-hero-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .dash-hero-btn {
    justify-content: center;
  }

  .dash-hero-figure {
    flex-basis: 100%;
    min-width: 0;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 12px;
  }

  .dash-hero-figure-value {
    font-size: 34px;
  }

  .dash-tiles {
    gap: 10px;
    margin-bottom: 16px;
  }

  .dash-tile {
    padding: 14px;
    border-radius: 16px;
  }

  .dash-tile-icon {
    width: 38px;
    height: 38px;
    border-radius: 11px;
  }

  .dash-tile-value {
    font-size: 24px;
  }

  .dash-tile-go {
    display: none;
  }

  .dash-filters {
    padding: 12px;
    margin-bottom: 16px;
  }

  .dash-filters-main,
  .dash-filters-side {
    width: 100%;
  }

  .dash-chips {
    flex-wrap: nowrap;
    overflow-x: auto;
    margin: 0 -12px;
    padding: 2px 12px 4px;
    scrollbar-width: none;
  }

  .dash-chips::-webkit-scrollbar {
    display: none;
  }

  .dash-filters .dash-year {
    max-width: none;
  }

  .money-tiles {
    gap: 8px;
    padding: 0 14px 8px;
  }

  .money-tile {
    padding: 10px;
  }

  .money-tile-icon {
    width: 32px;
    height: 32px;
  }

  .money-tile-value {
    font-size: 16px;
  }

  .dash-grid {
    gap: 16px;
  }

  .dash-card-head {
    padding: 16px 14px 10px;
  }

  .dash-card-body {
    padding: 0 8px 12px;
  }

  .desk-grid,
  .acc-body {
    padding: 4px 14px 16px;
  }

  .acc-role {
    grid-template-columns: 34px minmax(0, 1fr) 72px 40px;
    gap: 10px;
    padding: 8px 4px;
  }

  .recent-item {
    padding: 10px 6px;
  }

  .recent-when {
    display: none;
  }
}

/* No movement for viewers who ask for less of it */
@media (prefers-reduced-motion: reduce) {
  .dash-rise,
  .desk-card,
  .net-bar-part,
  .dash-blob,
  .dash-reel,
  .dash-hero::after {
    animation: none !important;
  }

  .dash-meter-fill,
  .acc-split-active,
  .acc-role-bar > span {
    transition: none !important;
  }
}
</style>
